import { BookOutlined, Star, StarBorder } from '@mui/icons-material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PauseIcon from '@mui/icons-material/Pause';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import { CallStatus } from '../../../../contexts/PhoneCallsContext/phone-calls.types';
import { usePhoneCalls } from '../../../../contexts/PhoneCallsContext/PhoneCallsContext';
import useDebounce from '../../../../hooks/useDebounce';
import {
  AgentDirectoryEntry,
  CallDirection,
  CallPurpose,
  SimplifiedMember,
} from '../../../../interfaces/calls';
import coreService from '../../../../services/core';
import { getCapitalizedWord } from '../../../../util/stringFormat';
import { Directory } from '../Directory/Directory';

interface IncomingCallModalProps {
  open: boolean;
}

export interface IncomingCallModalRef {
  resetCall: () => void;
}

export const IncomingCallModal = forwardRef<IncomingCallModalRef, IncomingCallModalProps>(
  ({ open }, ref) => {
    const [isSearchingMembers, setIsSearchingMembers] = useState<boolean>(false);
    const [members, setMembers] = useState<SimplifiedMember[]>([]);
    const notify = useNotify();

    const {
      agent,
      activeCallState,
      callDuration,
      isMuted,
      isOnHold,
      callLog,
      conference,
      selectedMember,
      isAwaitingRating,
      audioRating,
      callNotes,
      agentDirectory,
      agentDirectoryLastUpdatedAt,
      canInitiateTransfer,
      isDirectoryOpen,
      memberPhoneNumber,
      callPurpose,
      setCallPurpose,
      hangUpCall,
      muteCall,
      holdUnholdConference,
      closeIncomingCallModal,
      setAudioRating,
      setCallNotes,
      saveCallSummary,
      setIsDirectoryOpen,
      refreshAgentDirectory,
      initiateWarmTransfer,
      initiateBlindTransfer,
      setSelectedMember,
    } = usePhoneCalls();

    const searchUsersByTerm = useCallback(
      async ({
        searchTerm,
        target,
      }: {
        searchTerm?: string | null;
        target: 'providers' | 'members';
      }) => {
        if (!searchTerm) {
          return [];
        }
        const { data } = await coreService.fetchMembers({ searchTerm, target });
        return data.map((member) => ({
          id: member.id,
          phone: member.phone,
          name: member.fullName,
        }));
      },
      [],
    );

    const debouncedFetchMembers = useDebounce(
      useCallback(
        async (param: { searchTerm: string }) => {
          setIsSearchingMembers(true);
          setMembers(await searchUsersByTerm({ ...param, target: 'members' }));
          setIsSearchingMembers(false);
        },
        [searchUsersByTerm],
      ),
    );

    const onMemberChange = (member: SimplifiedMember | null) => {
      setSelectedMember(member);
    };

    const resetCall = useCallback(() => {
      setMembers([]);
    }, []);

    const formatCallDuration = useMemo(() => {
      const minutes = Math.floor(callDuration / 60);
      const seconds = callDuration % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }, [callDuration]);

    const handleToggleMuteMicrophone = useCallback(async () => {
      try {
        muteCall(!isMuted);
      } catch (error) {
        console.error('Error toggling mute:', error);
        notify('Failed to toggle mute', 'error');
      }
    }, [isMuted, muteCall, notify]);

    const handleToggleHold = useCallback(async () => {
      try {
        if (!memberPhoneNumber) {
          notify('No participant phone number available', 'error');
          return;
        }

        await holdUnholdConference(!isOnHold, memberPhoneNumber);
      } catch (error) {
        console.error('Error toggling hold:', error);
        notify('Failed to toggle hold', 'error');
      }
    }, [holdUnholdConference, isOnHold, notify, memberPhoneNumber]);

    const handleEndCall = useCallback(async () => {
      try {
        await hangUpCall();
      } catch (error) {
        console.error('Error ending call:', error);
        notify('Error ending call, but call has been disconnected locally', 'warning');
      }
    }, [hangUpCall, notify]);

    // Save call summary
    const handleSaveCallSummary = useCallback(async () => {
      await saveCallSummary();
      closeIncomingCallModal();
    }, [saveCallSummary, closeIncomingCallModal]);

    const handleOpenDirectory = useCallback(async () => {
      await refreshAgentDirectory();
      setIsDirectoryOpen(true);
    }, [refreshAgentDirectory, setIsDirectoryOpen]);

    const handleCloseDirectory = useCallback(() => {
      setIsDirectoryOpen(false);
    }, [setIsDirectoryOpen]);

    const handleWarmTransfer = useCallback(
      async (targetAgent: AgentDirectoryEntry) => {
        try {
          await initiateWarmTransfer(targetAgent);
        } catch (error) {
          console.error('Error initiating warm transfer:', error);
          notify('Failed to initiate warm transfer', 'error');
        }
      },
      [initiateWarmTransfer, notify],
    );

    const handleBlindTransfer = useCallback(
      async (targetAgent: AgentDirectoryEntry) => {
        try {
          await initiateBlindTransfer(targetAgent);
        } catch (error) {
          console.error('Error initiating blind transfer:', error);
          notify('Failed to initiate blind transfer', 'error');
        }
      },
      [initiateBlindTransfer, notify],
    );

    useImperativeHandle(ref, () => ({
      resetCall,
    }));

    return (
      <>
        <Dialog
          open={open}
          onClose={handleSaveCallSummary}
          maxWidth={isDirectoryOpen ? 'md' : 'sm'}
          fullWidth
          sx={{
            '& .MuiDialog-container': {
              overflow: 'visible',
            },
            '& .MuiDialog-container > .MuiPaper-elevation': {
              borderRadius: '12px',
              height: '90vh',
              maxHeight: '800px',
              minHeight: '600px',
              display: 'flex',
              flexDirection: 'row',
              width: isDirectoryOpen ? '920px' : undefined,
              position: 'relative',
              overflow: 'visible',
            },
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              borderRadius: '12px',
              overflow: 'hidden',
            }}>
            <DialogTitle
              sx={{
                backgroundColor: '#F5F5F5',
                paddingTop: '8px',
                paddingBottom: '8px',
                paddingLeft: '1rem',
                paddingRight: '1rem',
              }}>
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <IconButton
                  onClick={handleSaveCallSummary}
                  sx={{
                    width: '32px',
                    height: '32px',
                    padding: '4px',
                    marginRight: '8px',
                  }}>
                  <CloseIcon sx={{ width: '20px', height: '20px' }} />
                </IconButton>

                {/* Member Selector - Only show if no member is selected */}
                {!selectedMember ? (
                  <Autocomplete
                    className='member-selector'
                    options={members}
                    filterOptions={(x) => x}
                    onChange={(_, v) => onMemberChange(v)}
                    noOptionsText='No members found'
                    value={selectedMember}
                    sx={{
                      height: '30px',
                      borderRadius: '10px',
                      '&.member-selector .MuiInputBase-fullWidth.MuiOutlinedInput-root.MuiInputBase-root':
                        {
                          borderRadius: '10px',
                        },
                    }}
                    getOptionLabel={(option) => `${option.name} ID:${option.id}`}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        placeholder='Search a member'
                        onChange={(e) => debouncedFetchMembers({ searchTerm: e.target.value })}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <Box sx={{ marginRight: '33px', marginTop: '4px' }}>
                              {isSearchingMembers && <CircularProgress color='inherit' size={14} />}
                              {params.InputProps.endAdornment}
                            </Box>
                          ),
                          startAdornment: (
                            <>
                              <Avatar sx={{ color: 'action.active', mr: 1 }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          sx: {
                            '&& input.MuiAutocomplete-input': {
                              padding: '0 4px 0 6px',
                              width: '200px',
                            },
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component='li' {...props}>
                        <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                          {`${option.name?.[0]}${option.name?.[1]}`.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontSize='0.8rem'>{option.name}</Typography>
                          <Typography variant='caption' color='textSecondary'>
                            ID:{option.id} Phone:{option.phone}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                ) : (
                  <Box display='flex' alignItems='center' gap={1}>
                    <Typography variant='body1' sx={{ fontWeight: 500 }}>
                      {selectedMember.name}
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogTitle>

            <DialogContent
              sx={{
                padding: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}>
              {/* Call Information Section */}
              <Box
                display='flex'
                gap={2}
                pl={3}
                pr={3}
                pb='20px'
                pt={3}
                borderBottom='1px solid #CDCDCD'
                alignItems='center'
                justifyContent='center'
                height='15%'>
                <Box flex={1} display='flex' flexDirection='column'>
                  <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                    Phone Number
                  </Typography>
                  <TextField
                    disabled={true}
                    variant='outlined'
                    placeholder='Incoming call number'
                    size='small'
                    value={memberPhoneNumber.replaceAll('+1', '')}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', pr: '4px' }}>
                          <Typography variant='body2' color='textSecondary' sx={{ mr: 0.5 }}>
                            +1
                          </Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>

                <Box flex={1} display='flex' flexDirection='column'>
                  <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                    Call Purpose
                  </Typography>
                  <Autocomplete
                    disabled={
                      activeCallState.status === CallStatus.Ended ||
                      activeCallState.status === CallStatus.Ending
                    }
                    fullWidth
                    size='small'
                    options={Object.values(CallPurpose)}
                    getOptionLabel={(option) => getCapitalizedWord(option.split('_').join(' '))}
                    value={callPurpose}
                    onChange={(_, value) => setCallPurpose(value || CallPurpose.GENERAL_INQUIRY)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant='outlined'
                        placeholder='Select call purpose'
                        size='small'
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                    disableClearable
                  />
                </Box>
              </Box>

              {/* Notes Section - Show during call and after call ends */}

              <Box
                display='flex'
                flexDirection='column'
                p={3}
                flex={1}
                minHeight={0}
                sx={{ overflowY: 'scroll' }}>
                <Typography variant='subtitle1' alignSelf='flex-start'>
                  Notes
                </Typography>

                {/* Notes Input - Always visible */}
                <TextField
                  disabled={!callLog}
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  placeholder='Enter call notes...'
                  variant='outlined'
                  multiline
                  rows={3}
                  fullWidth
                />

                {/* Rating Section - Show after call ends - Compact */}
                {!activeCallState.call && callLog && isAwaitingRating && (
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    justifyContent='center'
                    mt={6}
                    flexShrink={0}>
                    <Typography variant='subtitle2' mb={0.5} textAlign='center'>
                      How was the audio?
                    </Typography>
                    <Box display='flex' alignItems='center' mb={0.5}>
                      <Rating
                        name='audio-rating'
                        value={audioRating}
                        onChange={(_, newValue) => setAudioRating(newValue)}
                        max={5}
                        size='medium'
                        icon={<Star fontSize='inherit' sx={{ color: '#FFC107' }} />}
                        emptyIcon={<StarBorder fontSize='inherit' sx={{ color: '#FFC107' }} />}
                      />
                    </Box>
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      width='100%'
                      maxWidth='180px'
                      mb={1}>
                      <Typography variant='caption' color='textSecondary' fontSize='11px'>
                        Very bad
                      </Typography>
                      <Typography variant='caption' color='textSecondary' fontSize='11px'>
                        Very good
                      </Typography>
                    </Box>
                    <Button
                      variant='contained'
                      onClick={handleSaveCallSummary}
                      size='small'
                      sx={{
                        backgroundColor: '#1A667A',
                        color: '#fff',
                        borderRadius: '6px',
                        minWidth: '180px',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: '#15505c',
                        },
                      }}
                      startIcon={<SaveAltIcon />}>
                      Save and Close
                    </Button>
                  </Box>
                )}
              </Box>
            </DialogContent>

            {!isAwaitingRating &&
              activeCallState.status !== CallStatus.Ended &&
              activeCallState.status !== CallStatus.Ending && (
                <DialogActions
                  hidden={isAwaitingRating}
                  sx={{
                    borderTop: '1px solid #CDCDCD',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '64px',
                    padding: '12px 25px',
                    flexShrink: 0,
                  }}>
                  {/* Call Control Bar */}
                  <Box display='flex' justifyContent='space-between' width='100%' minHeight='30px'>
                    {/* Call State */}
                    <Box display='flex' alignItems='center' gap={1}>
                      {isOnHold && (
                        <PauseIcon
                          sx={{
                            fontSize: '18px',
                            color: '#ff9800',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.5 },
                            },
                          }}
                        />
                      )}
                      <Typography
                        variant='body2'
                        sx={{
                          color: isOnHold ? '#ff9800' : 'text.secondary',
                          fontWeight: isOnHold ? 600 : 400,
                        }}>
                        {activeCallState.call &&
                          activeCallState.direction === CallDirection.INBOUND &&
                          !isOnHold &&
                          'On Call'}
                        {activeCallState.call &&
                          activeCallState.direction === CallDirection.INBOUND &&
                          isOnHold &&
                          'On Hold'}
                      </Typography>
                    </Box>

                    {/* Call Duration*/}
                    <Typography
                      variant='body2'
                      color='textSecondary'
                      sx={{ minWidth: 48, textAlign: 'center' }}>
                      {activeCallState.call &&
                        activeCallState.direction === CallDirection.INBOUND && (
                          <span>{formatCallDuration}</span>
                        )}
                    </Typography>
                  </Box>

                  <Box
                    display='flex'
                    borderRadius='32px'
                    border='1px solid #F5F5F5'
                    padding='10px'
                    width='60%'
                    justifyContent='space-between'>
                    {/* Mute/Unmute Button */}
                    <IconButton
                      disabled={
                        !activeCallState.call ||
                        activeCallState.direction !== CallDirection.INBOUND ||
                        !callLog
                      }
                      color={isMuted ? 'default' : 'success'}
                      onClick={handleToggleMuteMicrophone}
                      sx={{
                        background: isMuted ? '#eee' : '#4caf50',
                        color: '#fff',
                        '&:hover': { background: isMuted ? '#ddd' : '#388e3c' },
                        '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
                      }}>
                      {isMuted ? <MicOffIcon /> : <MicIcon />}
                    </IconButton>

                    {/* Hold Button */}
                    <IconButton
                      onClick={handleToggleHold}
                      disabled={
                        !activeCallState.call ||
                        activeCallState.direction !== CallDirection.INBOUND ||
                        !callLog
                      }
                      sx={{
                        color: isOnHold ? '#fff' : '#607d8b',
                        background: isOnHold ? '#ff9800' : '#f5f5f5',
                        borderRadius: '50%',
                        '&:hover': {
                          background: isOnHold ? '#f57c00' : 'rgba(0, 0, 0, 0.04)',
                        },
                        '&:disabled': { color: '#e0e0e0', background: '#f5f5f5' },
                      }}>
                      <PauseIcon />
                    </IconButton>

                    {/* End a Call Button */}
                    <IconButton
                      color='error'
                      onClick={handleEndCall}
                      disabled={
                        !activeCallState.call || activeCallState.direction !== CallDirection.INBOUND
                      }
                      sx={{
                        background: '#f44336',
                        color: '#fff',
                        '&:hover': { background: '#b71c1c' },
                        '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
                      }}>
                      <CallEndIcon />
                    </IconButton>
                  </Box>
                </DialogActions>
              )}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              right: '-50px',
              top: '10px',
              backgroundColor: canInitiateTransfer ? '#1A667A' : '#9e9e9e',
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
              padding: '12px 8px',
              boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.15)',
              transition: 'all 0.3s ease',
              cursor: canInitiateTransfer ? 'pointer' : 'not-allowed',
              '&:hover': canInitiateTransfer
                ? {
                    backgroundColor: '#15505c',
                  }
                : {},
            }}>
            <IconButton
              onClick={
                canInitiateTransfer
                  ? isDirectoryOpen
                    ? handleCloseDirectory
                    : handleOpenDirectory
                  : undefined
              }
              disabled={!canInitiateTransfer}
              sx={{
                width: '32px',
                height: '32px',
                color: '#fff',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                '&:disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
              title={
                !canInitiateTransfer
                  ? 'Transfer not available'
                  : isDirectoryOpen
                  ? 'Hide Directory'
                  : 'Show Directory'
              }>
              <BookOutlined sx={{ width: '24px', height: '24px' }} />
            </IconButton>
          </Box>

          {/* Directory Panel */}
          {isDirectoryOpen && agent && (
            <Directory
              agents={agentDirectory}
              currentAgent={agent}
              conference={conference}
              lastUpdatedAt={agentDirectoryLastUpdatedAt}
              onWarmTransfer={handleWarmTransfer}
              onBlindTransfer={handleBlindTransfer}
              onRefresh={refreshAgentDirectory}
              isLoading={false}
            />
          )}
        </Dialog>
      </>
    );
  },
);
